import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSortingHatQuestions, submitSortingHat } from '../api/game';
import '../styles/sorting-hat.css';

const houseMeta = {
  Gryffindor: { fa: 'گریفیندور', emblem: 'شیر', emblemEn: 'Lion', gradient: 'linear-gradient(135deg, #740001, #d3a625)' },
  Hufflepuff: { fa: 'هافلپاف', emblem: 'گورکن', emblemEn: 'Badger', gradient: 'linear-gradient(135deg, #111, #ffd800)' },
  Ravenclaw: { fa: 'ریونکلاو', emblem: 'عقاب', emblemEn: 'Eagle', gradient: 'linear-gradient(135deg, #0e1a40, #946b2d)' },
  Slytherin: { fa: 'اسلیترین', emblem: 'مار', emblemEn: 'Serpent', gradient: 'linear-gradient(135deg, #1a472a, #aaa)' }
};

const T = {
  fa: {
    loading: 'کلاه گروه‌بندی در حال آماده شدن است...',
    glow: 'کلاه گروه‌بندی تصمیمش را گرفت',
    lockedNote: 'این آزمون فقط یک‌بار انجام می‌شود و نتیجه از سمت سرور ذخیره شده است.',
    empty: 'سوالی برای آزمون تعریف نشده است.',
    title: 'آیین گروه‌بندی',
    subtitle: 'کلاه گروه‌بندی منتظر پاسخ‌های توست',
    desc: 'پاسخ‌ها فقط به سرور ارسال می‌شوند؛ انتخاب گروه سمت کاربر قابل تغییر نیست.',
    questionKicker: 'به ندای درونت گوش کن',
    prev: 'قبلی',
    next: 'بعدی',
    submit: 'اعلام نتیجه',
    submitting: 'در حال ثبت...',
    loadError: 'خطا در بارگذاری سوالات کلاه گروه‌بندی',
    validateError: 'لطفاً به همه سوال‌ها پاسخ بدهید',
    scoreTitle: 'نتیجه نهایی',
  },
  en: {
    loading: 'The Sorting Hat is getting ready...',
    glow: 'The Sorting Hat has made its decision',
    lockedNote: 'This test can only be taken once and the result has been saved on the server.',
    empty: 'No questions have been defined for this test.',
    title: 'Sorting Ceremony',
    subtitle: 'The Sorting Hat is waiting for your answers',
    desc: 'Answers are sent directly to the server; sorting choice cannot be modified client-side.',
    questionKicker: 'Listen to your inner voice',
    prev: 'Previous',
    next: 'Next',
    submit: 'Submit Answers',
    submitting: 'Submitting...',
    loadError: 'Error loading Sorting Hat questions',
    validateError: 'Please answer all questions',
    scoreTitle: 'Final Result',
  }
};

export default function SortingHatQuiz({ user, onCompleted }) {
  const { i18n } = useTranslation();
  const IS_PERSIAN = i18n.language === 'fa';
  const t = T[i18n.language || 'fa'] || T.en;

  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(user?.house ? { house: { name: user.house }, scores: user.sortingHatScores } : null);

  const alreadySorted = Boolean(user?.hasPassedSortingHat || user?.house || result?.house);
  const question = questions[current];
  const progress = useMemo(() => {
    if (!questions.length) return 0;
    return Math.round((answers.filter((answer) => answer !== undefined).length / questions.length) * 100);
  }, [answers, questions.length]);

  useEffect(() => {
    let mounted = true;
    async function fetchQuestions() {
      try {
        const response = await getSortingHatQuestions();
        if (mounted) setQuestions(response.data.questions || []);
      } catch (err) {
        if (mounted) setError(t.loadError);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchQuestions();
    return () => { mounted = false; };
  }, [t.loadError]);

  const chooseAnswer = (index) => {
    if (alreadySorted || submitting) return;
    const next = [...answers];
    next[current] = index;
    setAnswers(next);
    if (current < questions.length - 1) {
      setTimeout(() => setCurrent((value) => value + 1), 220);
    }
  };

  const submit = async () => {
    setError('');
    if (answers.length !== questions.length || answers.some((answer) => answer === undefined)) {
      setError(t.validateError);
      return;
    }

    setSubmitting(true);
    try {
      const response = await submitSortingHat(answers);
      setResult(response.data);
      onCompleted?.(response.data);
    } catch (err) {
      const data = err.response?.data;
      if (data?.alreadySorted) {
        setResult({ house: data.house, scores: data.scores });
        setError(data.message);
      } else {
        setError(data?.message || (IS_PERSIAN ? 'خطا در ثبت نتیجه آزمون' : 'Error submitting test result'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="sorting-shell"><div className="sorting-card">{t.loading}</div></div>;
  }

  if (result?.house?.name) {
    const meta = houseMeta[result.house.name] || {};
    const scores = result.scores || user?.sortingHatScores || {};
    return (
      <div className="sorting-shell">
        <section className="house-reveal" style={{ background: meta.gradient }}>
          <div className="hat-glow">{t.glow}</div>
          <h2>{IS_PERSIAN ? (meta.fa || result.house.farsiName || result.house.name) : result.house.name}</h2>
          <p>{IS_PERSIAN ? (result.house.description || 'از امروز مسیر جادویی تو با این گروه ادامه پیدا می‌کند.') : (result.house.englishDescription || 'From today on, your magical journey continues with this house.')}</p>
          <div className="house-emblem">{IS_PERSIAN ? (meta.emblem || result.house.animal || 'نشان خانه') : (meta.emblemEn || result.house.animalEnglish || 'Emblem')}</div>
        </section>

        <div className="sorting-card scores-card">
          <h3>{t.scoreTitle}</h3>
          {Object.entries(scores).map(([house, score]) => (
            <div className="score-row" key={house}>
              <span>{IS_PERSIAN ? (houseMeta[house]?.fa || house) : house}</span>
              <strong>{score}</strong>
              <div><i style={{ width: `${Math.min(100, Number(score || 0) * 7)}%` }} /></div>
            </div>
          ))}
          <p className="locked-note">{t.lockedNote}</p>
        </div>
      </div>
    );
  }

  if (!questions.length) {
    return <div className="sorting-shell"><div className="sorting-card">{t.empty}</div></div>;
  }

  return (
    <div className="sorting-shell">
      <section className="sorting-hero">
        <div>
          <span>{t.title}</span>
          <h2>{t.subtitle}</h2>
          <p>{t.desc}</p>
        </div>
        <div className="hat-orb">Sorting Hat</div>
      </section>

      <div className="sorting-progress">
        <span>{IS_PERSIAN ? `سوال ${current + 1} از ${questions.length}` : `Question ${current + 1} of ${questions.length}`}</span>
        <strong>{progress}%</strong>
        <div><i style={{ width: `${progress}%` }} /></div>
      </div>

      {error && <div className="sorting-error">{error}</div>}

      <article className="sorting-card question-card">
        <p className="question-kicker">{t.questionKicker}</p>
        <h3>{IS_PERSIAN ? (question.farsiQuestion || question.question) : (question.question || question.farsiQuestion)}</h3>
        <div className="answer-grid">
          {question.answers.map((answer) => (
            <button
              key={answer.index}
              className={answers[current] === answer.index ? 'answer-card selected' : 'answer-card'}
              onClick={() => chooseAnswer(answer.index)}
              type="button"
            >
              {answer.image && <span className="answer-image" style={{ backgroundImage: `url(${answer.image})` }} />}
              <strong>{IS_PERSIAN ? (answer.farsi || answer.text) : (answer.text || answer.farsi)}</strong>
              {IS_PERSIAN && answer.farsi && <small>{answer.text}</small>}
            </button>
          ))}
        </div>
      </article>

      <div className="sorting-actions">
        <button type="button" onClick={() => setCurrent((value) => Math.max(0, value - 1))} disabled={current === 0 || submitting}>{t.prev}</button>
        {current < questions.length - 1 ? (
          <button type="button" onClick={() => setCurrent((value) => Math.min(questions.length - 1, value + 1))} disabled={answers[current] === undefined || submitting}>{t.next}</button>
        ) : (
          <button type="button" className="primary" onClick={submit} disabled={submitting || progress < 100}>{submitting ? t.submitting : t.submit}</button>
        )}
      </div>
    </div>
  );
}
